import { z } from 'zod';
import { CrewCategoryCode, EquipmentCategory } from './marketplace.enums';
import { MoneySchema, PaginationParamsSchema } from '../common';
import { VendorRecentReviewSchema } from '../booking/review.schemas';
import { VendorTeamSize } from '../user/user.enums';

export const ListVendorsQuerySchema = z.object({
  category: z.string().optional(),
  /**
   * Optional service-category code filter (vendor “role” alias).
   * Same matching as `category`. Vendors may have multiple categories.
   * Known codes include: sound, lighting, video, staging, av, logistics.
   */
  role: z.string().trim().min(1).max(100).optional(),
  /** Rate-card bounds in major units (naira). */
  minRate: z.coerce.number().int().min(0).optional(),
  maxRate: z.coerce.number().int().min(0).optional(),
  availabilityDate: z.string().optional(),
  /** Case-insensitive match on name, company, role/skill, or equipment */
  search: z.string().trim().min(2).max(100).optional(),
  ...PaginationParamsSchema.shape,
});

export const PublicVendorListEquipmentItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  category: z.enum(EquipmentCategory),
  notes: z.string().nullable(),
});

export const PublicVendorListCategorySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  label: z.string(),
});

export const ServiceCategoryListResponseSchema = z.object({
  items: z.array(PublicVendorListCategorySchema),
});

// ─── Crew onboarding: categories, baselines and skills ────────────────

/**
 * One skill inside a category tab. `crewCategoryId` is required: a skill is
 * always reached through its category, which is the base of the taxonomy.
 */
export const CrewSkillSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  label: z.string(),
  crewCategoryId: z.uuid(),
});

export const CrewCategorySchema = z.object({
  id: z.uuid(),
  code: z.enum(CrewCategoryCode),
  label: z.string(),
  /** What a booking of this category must deliver. */
  baseline: z.string(),
  skills: z.array(CrewSkillSchema),
});

export const CrewCategoryListResponseSchema = z.object({
  items: z.array(CrewCategorySchema),
});

/**
 * Writes one category tab: which skills the crew member has in it. Experience
 * level and fee range are NOT here --- they describe the person, not the job,
 * so a videographer-photographer with six years has six years in both. They
 * live on the vendor profile and its rate card.
 *
 * `skillLabels` carries skills the crew member typed in; they are created
 * under this category.
 */
export const UpsertVendorCrewCategoryRequestSchema = z.object({
  crewCategoryCode: z.enum(CrewCategoryCode),
  skillIds: z.array(z.uuid()).optional(),
  skillLabels: z.array(z.string().trim().min(1).max(64)).optional(),
});

/** A category a crew member works in, with their skills in it. */
export const VendorCrewCategorySchema = z.object({
  crewCategoryId: z.uuid(),
  crewCategoryCode: z.enum(CrewCategoryCode),
  crewCategoryLabel: z.string(),
  baseline: z.string(),
  skills: z.array(CrewSkillSchema),
});

export const VendorCrewCategoryListResponseSchema = z.object({
  items: z.array(VendorCrewCategorySchema),
});

export const PublicVendorListPortfolioItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  mediaUrl: z.url().nullable(),
  externalUrl: z.url().nullable(),
  createdAt: z.iso.datetime(),
});

export const PublicVendorListRateCardItemSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  amount: MoneySchema,
  notes: z.string().nullable(),
});

/**
 * Enum values match the Prisma `MessageKind` column. Live wire type
 * for hiring-thread messages.
 */
export const HiringThreadMessageKindSchema = z.enum(['USER', 'SYSTEM']);

/** Matches the Prisma `MessageDeliveryStatus` column. */
export const HiringThreadMessageDeliveryStatusSchema = z.enum(['SENT', 'DELIVERED', 'READ']);

/**
 * Structured payload attached to a `SYSTEM` message. Discriminated on
 * `kind` — the set is intentionally narrow. Extend the union as new
 * automated messages get added (booking accepted, cancelled, etc.).
 */
export const HiringThreadSystemMessagePayloadSchema = z.object({
  kind: z.literal('EVENT_RESCHEDULED'),
  eventId: z.string(),
  fromStart: z.iso.datetime(),
  toStart: z.iso.datetime(),
});

/**
 * Message attachment shape rendered by the hiring-thread inbox +
 * socket fan-out. `fileName` is normalised server-side (falls back to
 * the storage key basename if the client didn't send one).
 */
export const HiringThreadMessageAttachmentSchema = z.object({
  id: z.string(),
  url: z.url(),
  mimeType: z.string(),
  sizeBytes: z.number().int().positive(),
  fileName: z.string(),
  createdAt: z.iso.datetime(),
});

/**
 * Full hiring-thread message view — the shape returned by the socket
 * post ACK and by the fan-out event. Kept in one schema so a shared FE
 * renderer can accept either source.
 */
export const HiringThreadMessageSchema = z.object({
  id: z.string(),
  threadId: z.string(),
  /** `null` for SYSTEM messages (Livdot-authored). */
  senderUserId: z.string().nullable(),
  messageKind: HiringThreadMessageKindSchema,
  systemPayload: HiringThreadSystemMessagePayloadSchema.nullable(),
  body: z.string(),
  sentAt: z.iso.datetime(),
  deliveryStatus: HiringThreadMessageDeliveryStatusSchema,
  readAt: z.iso.datetime().nullable(),
  attachments: z.array(HiringThreadMessageAttachmentSchema),
});

/** ACK for `hiring_thread.message.post`. */
export const HiringThreadPostAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    message: HiringThreadMessageSchema,
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum([
      'unauthorized',
      'invalid_payload',
      'forbidden',
      'not_found',
      'messaging_closed',
      'rate_limited',
      'internal',
    ]),
    message: z.string(),
    retryAfterSeconds: z.number().int().nonnegative().optional(),
  }),
]);

/** ACK for `hiring_thread.message.read`. */
export const HiringThreadReadAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    markedCount: z.number().int().nonnegative(),
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['unauthorized', 'invalid_payload', 'forbidden', 'not_found', 'internal']),
    message: z.string(),
  }),
]);

/** ACK for `hiring_thread.join` / `hiring_thread.leave`. */
export const HiringThreadJoinAckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    threadId: z.string(),
  }),
  z.object({
    ok: z.literal(false),
    code: z.enum(['unauthorized', 'invalid_payload', 'forbidden', 'not_found']),
    message: z.string(),
  }),
]);

/** One element of `ListVendorsResponse.vendors`. */
export const PublicVendorListItemSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  displayName: z.string(),
  avatarUrl: z.url().nullable(),
  vendorIsVerified: z.boolean(),
  companyName: z.string().nullable(),
  bio: z.string().nullable(),
  yearsExperience: z.number().int().nullable(),
  teamSize: z.enum(VendorTeamSize).nullable(),
  maxDistanceKm: z.number().int().nullable(),
  isAvailable: z.boolean(),
  averageRating: z.string(),
  totalReviews: z.number().int(),
  completionRate: z.string(),
  equipmentManifest: z.array(PublicVendorListEquipmentItemSchema),
  serviceCategories: z.array(PublicVendorListCategorySchema),
  portfolio: z.array(PublicVendorListPortfolioItemSchema),
  equipmentItems: z.array(PublicVendorListCategorySchema),
  rateCard: z.array(PublicVendorListRateCardItemSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  recentReviews: z.array(VendorRecentReviewSchema).optional(),
});
